import React, { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, Alert, Button } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import ArticleIcon from '@mui/icons-material/Article';
import { getCurrentUser, isAdmin } from '../../services/authService';
import GuardrailDashboardTab from './GuardrailDashboardTab';
import GuardrailTierClassificationTab from './GuardrailTierClassificationTab';
import GuardrailDataUploadTab from './GuardrailDataUploadTab';
import GuardrailCriteriaBreakdownTab from './GuardrailCriteriaBreakdownTab';

const TAB_NAMES = ['dashboard', 'tier-classification', 'criteria-breakdown', 'data-upload'];

const GuardrailView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'dashboard';
  const initialIndex = TAB_NAMES.includes(tabParam) ? TAB_NAMES.indexOf(tabParam) : 0;
  const [activeTab, setActiveTab] = useState(initialIndex);
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);

  useEffect(() => {
    const idx = TAB_NAMES.includes(tabParam) ? TAB_NAMES.indexOf(tabParam) : 0;
    setActiveTab(idx);
  }, [tabParam]);

  const handleConfigSaved = () => setDashboardRefreshKey(k => k + 1);

  const user = getCurrentUser();
  const isAllowed = isAdmin() || user?.guardrailAccess === true;

  if (!isAllowed) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 480 }}>
          You don't have permission to access Guardrail. Contact an administrator if you need access.
        </Alert>
      </Box>
    );
  }

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
    const next = new URLSearchParams(searchParams);
    next.set('tab', TAB_NAMES[newValue]);
    setSearchParams(next);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', bgcolor: '#f3f6fa', minHeight: '100%', borderRadius: 2, p: 0 }}>
      {/* Heading */}
      <Box
        sx={{
          background: 'linear-gradient(105deg, #12365a 0%, #1d4f7a 100%)',
          borderRadius: '12px 12px 0 0',
          px: 3, py: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 720, fontSize: 20, letterSpacing: '-0.3px', color: '#fff', lineHeight: 1.2 }}>
            Project Guardrail
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#cfe0eb', mt: 0.3 }}>
            Client instance tier classification and limit monitoring
          </Typography>
        </Box>
        <Button
          component="a"
          href="/docs/HighQ_Guardrails_1_Pager.html"
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<ArticleIcon />}
          size="small"
          sx={{
            color: '#fff', borderColor: 'rgba(255,255,255,0.4)', border: '1px solid',
            borderRadius: '8px', fontSize: 13, fontWeight: 600, px: 1.5, py: 0.6,
            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
          }}
        >
          1-Pager
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #dfe5ee', px: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': { fontSize: 13.5, fontWeight: 650, color: '#455168', minHeight: 44 },
            '& .Mui-selected': { color: '#12365a' },
            '& .MuiTabs-indicator': { bgcolor: '#12365a', height: 3 },
          }}
        >
          <Tab label="Dashboard" />
          <Tab label="Tier Classification" />
          <Tab label="Criteria Breakdown" />
          <Tab label="Data Upload" />
        </Tabs>
      </Box>

      <Box sx={{ p: 2 }}>
        {activeTab === 0 && <GuardrailDashboardTab refreshKey={dashboardRefreshKey} />}
        {activeTab === 1 && <GuardrailTierClassificationTab onConfigSaved={handleConfigSaved} />}
        {activeTab === 2 && <GuardrailCriteriaBreakdownTab refreshKey={dashboardRefreshKey} />}
        {activeTab === 3 && <GuardrailDataUploadTab />}
      </Box>
    </Box>
  );
};

export default GuardrailView;
